import { useState, useEffect, useCallback, useRef } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { supabase } from '@/integrations/supabase/client'
import { Tables } from '@/integrations/supabase/types'
import { cloneCommunityCourse } from '@/utils/communityLibrary'

export type EduHub = Tables<'facilities'>
export type EduClass = Omit<Tables<'classes'>, 'enrollment_mode'> & {
    teacher_name?: string | null
    student_count?: number
    origin?: string | null
    is_community_published?: boolean | null
    community_suggested?: boolean | null
    is_published?: boolean | null
    enrollment_mode?: 'all' | 'targeted' | string | null
}

export interface EduStudent {
    id: string
    name: string | null
    email: string
    facility_role: string | null
    created_at: string | null
}

export type EduTeacher = Tables<'school_teachers'> & {
    name: string | null
    email: string
}

export interface EduProgress {
    user_id: string
    category_id: string
    mastery_score: number | null
    lessons_completed: any
    last_activity_at: string | null
}

export type EduKnowledge = Tables<'class_knowledge'>

export interface EduGroup {
    id: string
    facility_id: string
    owner_id: string
    name: string
    description: string | null
    is_active: boolean | null
    created_at: string | null
}

export interface EduGroupMember {
    id: string
    group_id: string
    student_id: string
    assigned_by: string
    created_at: string | null
}

export interface EduCoTeacher {
    id: string
    class_id: string
    teacher_id: string
    teacher_name?: string | null
    added_by: string
    role: string
    created_at: string | null
}

export interface EduStaffMember {
    id: string
    name: string | null
    email: string
    facility_role: string | null
}

export type TransferStatus = 'pending' | 'accepted' | 'declined' | 'cancelled' | 'expired'

export interface EduCourseTransfer {
    id: string
    class_id: string
    class_name?: string | null
    from_teacher_id: string
    to_teacher_id: string
    requested_by: string
    status: TransferStatus
    reason: string | null
    created_at: string | null
    expires_at: string | null
    responded_at: string | null
}

export interface EduStudentTransfer {
    id: string
    student_id: string
    student_name?: string | null
    from_teacher_id: string | null
    to_teacher_id: string | null
    requested_by: string
    status: TransferStatus
    reason: string | null
    created_at: string | null
    expires_at: string | null
    responded_at: string | null
}

export function useEducationAdmin() {
    const { user, userProfile } = useAuth()
    const [hub, setHub] = useState<EduHub | null>(null)
    const [classes, setClasses] = useState<EduClass[]>([])
    const [students, setStudents] = useState<EduStudent[]>([])
    const [teachers, setTeachers] = useState<EduTeacher[]>([])
    const [progress, setProgress] = useState<EduProgress[]>([])
    // Silo model: members enrolled in any class I own OR co-teach (teachers only)
    const [myStudentIds, setMyStudentIds] = useState<Set<string>>(new Set())
    const [groups, setGroups] = useState<EduGroup[]>([])
    const [groupMembers, setGroupMembers] = useState<EduGroupMember[]>([])
    const [coTeachers, setCoTeachers] = useState<EduCoTeacher[]>([])
    const [facilityStaff, setFacilityStaff] = useState<EduStaffMember[]>([])
    const [courseTransfers, setCourseTransfers] = useState<EduCourseTransfer[]>([])
    const [studentTransfers, setStudentTransfers] = useState<EduStudentTransfer[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    const facilityId = userProfile?.facility_id
    const facilityRole = userProfile?.facility_role

    const fetchAll = useCallback(async () => {
        if (!user || !facilityId) { setLoading(false); return }
        setLoading(true)
        setError(null)

        try {
            // 1. Hub details
            const { data: hubData, error: hubErr } = await supabase
                .from('facilities')
                .select('*')
                .eq('id', facilityId)
                .single()
            if (hubErr) throw hubErr
            setHub(hubData)

            // 2. Classes — silo model: teachers get their own courses PLUS any
            //    course shared with them (co-teaching); principals get all.
            //    Grants are fetched first so the OR filter can include shared
            //    classes (RLS permits both reads via is_class_staff).
            let coTaughtClassIds: string[] = []
            if (facilityRole === 'teacher') {
                const { data: grantData } = await supabase
                    .from('course_co_teachers')
                    .select('class_id')
                    .eq('teacher_id', user.id)
                coTaughtClassIds = (grantData || []).map((g: any) => g.class_id)
            }

            let classQuery = supabase
                .from('classes')
                .select('*')
                .eq('facility_id', facilityId)
                .eq('is_active', true)
                .order('created_at', { ascending: false })

            if (facilityRole === 'teacher') {
                if (coTaughtClassIds.length > 0) {
                    classQuery = classQuery.or(`teacher_id.eq.${user.id},id.in.(${coTaughtClassIds.join(',')})`)
                } else {
                    classQuery = classQuery.eq('teacher_id', user.id)
                }
            }

            const { data: classData, error: classErr } = await classQuery
            if (classErr) throw classErr
            const rawClasses = classData || []

            // Enrich with teacher names + student counts
            const enriched = await Promise.all(
                rawClasses.map(async (cls) => {
                    const [teacherRes, countRes] = await Promise.all([
                        cls.teacher_id
                            ? supabase.from('users').select('name').eq('id', cls.teacher_id).single()
                            : Promise.resolve({ data: null, error: null }),
                        supabase
                            .from('class_enrollments')
                            .select('student_id', { count: 'exact', head: true })
                            .eq('class_id', cls.id),
                    ])
                    return {
                        ...cls,
                        teacher_name: teacherRes.data?.name ?? null,
                        student_count: countRes.count ?? 0,
                    }
                })
            )
            setClasses(enriched)

            // 3. Students (members of this facility)
            const { data: stuData, error: stuErr } = await supabase
                .from('users')
                .select('id, name, email, facility_role, created_at')
                .eq('facility_id', facilityId)
                .eq('facility_role', 'member')
                .order('name', { ascending: true })
            if (stuErr) throw stuErr
            setStudents(stuData || [])

            // 3.5 Silo scope helpers. myStudentIds = members enrolled in any
            //     class I own OR co-teach (mirrors is_teacher_student()).
            //     grantScopeIds = classes whose co-teacher grants I manage:
            //     teachers = own classes; admins = whole facility.
            const myClassIds = rawClasses
                .filter(c => c.teacher_id === user.id || coTaughtClassIds.includes(c.id))
                .map(c => c.id)
            const grantScopeIds = facilityRole === 'admin'
                ? rawClasses.map(c => c.id)
                : rawClasses.filter(c => c.teacher_id === user.id).map(c => c.id)
            if (facilityRole === 'teacher') {
                if (myClassIds.length === 0) {
                    setMyStudentIds(new Set())
                } else {
                    const { data: enrData, error: enrErr } = await supabase
                        .from('class_enrollments')
                        .select('student_id')
                        .in('class_id', myClassIds)
                    if (enrErr) throw enrErr
                    setMyStudentIds(new Set((enrData || []).map((r: any) => r.student_id)))
                }
            }

            // 4. Teacher roster — principals only
            if (facilityRole === 'admin') {
                const { data: tchData, error: tchErr } = await supabase
                    .from('school_teachers')
                    .select('*')
                    .eq('facility_id', facilityId)
                if (tchErr) throw tchErr

                const enrichedTeachers = await Promise.all(
                    (tchData || []).map(async (t) => {
                        const { data: u } = await supabase.from('users').select('name, email').eq('id', t.teacher_id).single()
                        return {
                            ...t,
                            name: u?.name ?? null,
                            email: u?.email ?? '',
                        }
                    })
                )
                setTeachers(enrichedTeachers)
            }

            // 5. Progress — fetch ALL rows; no cap (classroom scale: 25 students × 12 subjects = 300+)
            const studentIds = (stuData || []).map(s => s.id)
            if (studentIds.length > 0) {
                const { data: progData, error: progErr } = await supabase
                    .from('student_subject_progress')
                    .select('user_id, category_id, mastery_score, lessons_completed, last_activity_at')
                    .in('user_id', studentIds)
                    .order('last_activity_at', { ascending: false })
                if (progErr) throw progErr
                setProgress((progData || []) as EduProgress[])
            } else {
                setProgress([])
            }

            // 6. Internal groups — RLS scopes this to "my own groups" for a
            //    teacher, "all groups in the facility" for admin. Null/no group
            //    is the default; groups are opt-in organisation.
            const { data: grpData, error: grpErr } = await supabase
                .from('facility_groups')
                .select('*')
                .eq('facility_id', facilityId)
                .eq('is_active', true)
                .order('name', { ascending: true })
            if (grpErr) throw grpErr
            setGroups((grpData || []) as EduGroup[])

            const grpIds = (grpData || []).map((g: any) => g.id)
            if (grpIds.length > 0) {
                const { data: memData, error: memErr } = await supabase
                    .from('group_members')
                    .select('*')
                    .in('group_id', grpIds)
                if (memErr) throw memErr
                setGroupMembers((memData || []) as EduGroupMember[])
            } else {
                setGroupMembers([])
            }

            // 7. Co-teacher grants in my management scope — teachers: classes
            //    they own as primary; admins: every grant in the facility
            //    (RLS permits both). Grants where I am the co-teacher are not
            //    needed here — the classes query already includes those
            //    classes via the OR filter.
            if (grantScopeIds.length > 0) {
                const { data: ctData, error: ctErr } = await supabase
                    .from('course_co_teachers')
                    .select('*')
                    .in('class_id', grantScopeIds)
                if (ctErr) throw ctErr
                const enrichedCt = await Promise.all(
                    (ctData || []).map(async (ct: any) => {
                        const { data: u } = await supabase.from('users').select('name').eq('id', ct.teacher_id).single()
                        return { ...ct, teacher_name: u?.name ?? null }
                    })
                )
                setCoTeachers(enrichedCt as EduCoTeacher[])
            } else {
                setCoTeachers([])
            }

            // 7.5 Colleague picker — other teachers/admins in my facility.
            const { data: staffData, error: staffErr } = await supabase
                .from('users')
                .select('id, name, email, facility_role')
                .eq('facility_id', facilityId)
                .in('facility_role', ['teacher', 'admin'])
                .neq('id', user.id)
                .order('name', { ascending: true })
            if (staffErr) throw staffErr
            setFacilityStaff((staffData || []) as EduStaffMember[])

            // 8. Transfer requests involving me (either side) or my facility
            //    (admin) — RLS already scopes this, no extra filter needed.
            const nameLookup = new Map<string, string>()
            ;(stuData || []).forEach((s: any) => nameLookup.set(s.id, s.name || s.email))
            ;(staffData || []).forEach((s: any) => nameLookup.set(s.id, s.name || s.email))

            const { data: ctrData, error: ctrErr } = await supabase
                .from('course_transfer_requests')
                .select('*, classes(name)')
                .order('created_at', { ascending: false })
                .limit(50)
            if (ctrErr) throw ctrErr
            setCourseTransfers(((ctrData || []) as any[]).map(r => ({
                ...r, class_name: r.classes?.name ?? null,
            })) as EduCourseTransfer[])

            const { data: strData, error: strErr } = await supabase
                .from('student_teacher_transfer_requests')
                .select('*')
                .order('created_at', { ascending: false })
                .limit(50)
            if (strErr) throw strErr
            setStudentTransfers(((strData || []) as any[]).map(r => ({
                ...r, student_name: nameLookup.get(r.student_id) ?? null,
            })) as EduStudentTransfer[])

        } catch (err: unknown) {
            console.error('[useEducationAdmin]', err)
            setError((err as Error).message || 'Failed to load education data.')
        } finally {
            setLoading(false)
        }
    }, [user?.id, facilityId, facilityRole])

    useEffect(() => {
        if (user && facilityId) fetchAll()
        else setLoading(false)
    }, [user?.id, facilityId, fetchAll])

    const inviteTeacher = async (email: string, name?: string, resend?: boolean) => {
        const { data, error } = await supabase.functions.invoke('invite-teacher', { body: { email, name, resend } })
        if (error) {
            let parsed: any = null
            try {
                parsed = await (error as any).context?.json?.()
            } catch { /* ignore parse failures */ }
            if (parsed?.requires_transfer) {
                const e: any = new Error(parsed.error || 'Transfer required')
                e.requires_transfer = true
                e.transfer_payload = parsed
                throw e
            }
            throw new Error(parsed?.error || error.message)
        }
        if (data?.error) throw new Error(data.error)
        await fetchAll()
    }

    const inviteStudent = async (email: string, name?: string, classId?: string, resend?: boolean) => {
        const { data, error } = await supabase.functions.invoke('invite-student', {
            body: { email, name, class_id: classId || null, resend },
        })
        // FunctionsHttpError wraps non-2xx responses; parse the body for a friendly message
        if (error) {
            let parsed: any = null
            try {
                // error.context is the raw Response object
                parsed = await (error as any).context?.json?.()
            } catch { /* ignore parse failures */ }
            if (parsed?.requires_transfer) {
                // Rethrow as a structured object so callers can show the transfer UI
                const e: any = new Error(parsed.error || 'Transfer required')
                e.requires_transfer = true
                e.transfer_payload = parsed
                throw e
            }
            // Friendly message extracted from body, or fallback to SDK message
            throw new Error(parsed?.error || error.message)
        }
        if (data?.error) throw new Error(data.error)
        await fetchAll()
    }

    const createClass = async (payload: Partial<EduClass> & {
        category_id: string
        source_class_id?: string
        origin?: string   // request discriminator ('community' = library clone) — NEVER a row value
    }) => {
        if (!payload.category_id) {
            throw new Error('A subject category must be selected to create a class.')
        }

        // ── Single insert path for community clones — delegate to shared util ──
        if (payload.origin === 'community' && payload.source_class_id) {
            const res = await cloneCommunityCourse(
                payload.source_class_id,
                { teacherId: payload.teacher_id || user!.id, facilityId: facilityId! },
                payload.name
            )
            if (res.error) throw new Error(res.error)
            await fetchAll()
            return
        }

        // ── Master catalog copy or custom class ──
        let masterPlan: any = null
        let essayMode: string | null = null
        let curriculumTrack: string | null = null
        let subjectGroup: string | null = null
        let knowledgeContext: string | null = null
        let rowOrigin: 'master' | 'teacher_created' = 'teacher_created'

        if (payload.source_class_id) {
            const { data: master, error: masterErr } = await supabase
                .from('classes')
                .select('module_plan, essay_mode, curriculum_track, subject_group, knowledge_context, name')
                .eq('id', payload.source_class_id)
                .maybeSingle()
            if (masterErr) throw masterErr
            if (!master) throw new Error('Master course not found.')
            masterPlan = master.module_plan
            essayMode = master.essay_mode
            curriculumTrack = master.curriculum_track
            subjectGroup = master.subject_group
            knowledgeContext = master.knowledge_context
            // Auto-name: "{Master name} — {Hub name}" if no custom name given
            if (!payload.name || payload.name === (master as any).name) {
                payload.name = `${(master as any).name} — ${hub?.name || 'Class'}`
            }
            rowOrigin = 'master'
        }

        const { teacher_name, student_count, ...dbPayload } = payload;

        const { data: insertedClass, error } = await (supabase.from('classes') as any).insert({
            name: dbPayload.name ?? '',
            category_id: payload.category_id,
            description: (dbPayload as any).description ?? '',
            facility_id: facilityId!,
            teacher_id: payload.teacher_id || user!.id,
            is_active: true,
            module_plan: masterPlan,
            essay_mode: essayMode,
            curriculum_track: curriculumTrack,
            subject_group: subjectGroup ?? 'General',
            knowledge_context: knowledgeContext,
            source_class_id: payload.source_class_id ?? null,
            origin: rowOrigin,
            is_community_published: false,
        }).select('id').maybeSingle()
        if (error) throw error

        if (payload.source_class_id && insertedClass?.id) {
            try {
                await supabase.rpc('copy_class_lesson_media', {
                    p_source_class_id: payload.source_class_id,
                    p_target_class_id: insertedClass.id
                })
            } catch (mediaErr) {
                console.warn('[useEducationAdmin] Failed to copy lesson media:', mediaErr)
            }
        }
        await fetchAll()
    }



    const updateClassModulePlan = async (classId: string, plan: any[]) => {
        const { error } = await supabase
            .from('classes')
            .update({ module_plan: plan })
            .eq('id', classId)
        if (error) throw error
        await fetchAll()
    }

    const fetchKnowledge = useCallback(async (classId: string) => {
        const { data, error } = await supabase
            .from('class_knowledge')
            .select('*')
            .eq('class_id', classId)
            .order('created_at', { ascending: false })
        if (error) throw error
        return data as EduKnowledge[]
    }, [])

        const uploadKnowledge = async (classId: string, file: File) => {
        const fileExt = file.name.split('.').pop()
        const fileName = `${classId}/${Math.random().toString(36).slice(2)}.${fileExt}`
        const filePath = `${fileName}`

        // ── Text extraction: .txt/.md inline (zero deps). PDF/DOCX stay null
        //    until the extraction upgrade — rows without extracted_text are
        //    invisible to the AI (loader filters nulls at teach time).
        let extractedText: string | null = null
        const isTextFile = file.type.startsWith('text/') || /\.(txt|md)$/i.test(file.name)
        if (isTextFile) {
            try { extractedText = (await file.text()).trim() || null } catch { extractedText = null }
        }

        const { error: uploadErr } = await supabase.storage
            .from('class-knowledge')
            .upload(filePath, file)
        if (uploadErr) throw uploadErr

        const { data: urlData } = supabase.storage
            .from('class-knowledge')
            .getPublicUrl(filePath)

        const { error: dbErr } = await (supabase.from('class_knowledge') as any).insert({
            class_id: classId,
            file_name: file.name,
            file_url: urlData.publicUrl,
            extracted_text: extractedText,
            title: file.name.replace(/\.[^.]+$/, ''),
            char_count: extractedText ? extractedText.length : null,
            added_by: user?.id ?? null,
            is_active: true
        })
        if (dbErr) throw dbErr
        await fetchAll()
    }

        const deleteKnowledge = async (id: string, path?: string | null) => {
        const { error: dbErr } = await supabase
            .from('class_knowledge')
            .delete()
            .eq('id', id)
        if (dbErr) throw dbErr

        // Paste-text docs have no storage object — path is null/empty
        if (path) {
            const storagePath = path.split('/public/class-knowledge/')[1] || path
            await supabase.storage.from('class-knowledge').remove([storagePath])
        }
        await fetchAll()
    }

    // ── Paste-path knowledge: no file, straight text. Same table the AI reads. ──
    const addKnowledgeText = async (classId: string, title: string, content: string) => {
        const cleanTitle = (title || '').trim() || 'Untitled document'
        const clean = (content || '').trim()
        if (!clean) throw new Error('Nothing to add — paste the document text first.')
        if (clean.length > 200000) throw new Error('Document too large — max ~200,000 characters. Split it into parts.')
        const { error } = await (supabase.from('class_knowledge') as any).insert({
            class_id: classId,
            file_name: cleanTitle,          // NOT NULL column — title stands in
            file_url: null,                 // no stored file for pasted docs
            extracted_text: clean,
            title: cleanTitle,
            char_count: clean.length,
            added_by: user?.id ?? null,
            is_active: true,
        })
        if (error) throw error
        await fetchAll()
    }

    const importStudents = async (data: { name: string; email: string }[], classId?: string) => {
        const results = { success: 0, failed: 0, errors: [] as string[] }
        for (const student of data) {
            try {
                await inviteStudent(student.email, student.name, classId)
                results.success++
            } catch (err) {
                results.failed++
                results.errors.push(`${student.email}: ${(err as Error).message}`)
            }
            await new Promise(r => setTimeout(r, 300))
        }
        return results
    }

    const fetchStudentLogs = useCallback(async (studentId: string, classId?: string) => {
        let q = supabase
            .from('student_lesson_logs')
            .select('*')
            .eq('user_id', studentId)
            .order('completed_at', { ascending: false })
            .limit(20)

        if (classId) q = q.eq('class_id', classId)

        const { data, error } = await q
        if (error) throw error
        return data as any[]
    }, [supabase])

    const avgMastery = progress.length > 0
        ? Math.round(
            progress.reduce((sum, p) => sum + (p.mastery_score ?? 0), 0) / progress.length
        )
        : 0

    // ── Internal groups ──────────────────────────────────────────────────────

    const createGroup = useCallback(async (name: string, description?: string) => {
        const { error } = await (supabase.from('facility_groups') as any).insert({
            facility_id: facilityId!,
            owner_id: user!.id,
            name: name.trim(),
            description: description?.trim() || null,
        })
        if (error) throw error
        await fetchAll()
    }, [facilityId, user?.id, fetchAll])

    const deleteGroup = useCallback(async (groupId: string) => {
        const { error } = await supabase.from('facility_groups').delete().eq('id', groupId)
        if (error) throw error
        await fetchAll()
    }, [fetchAll])

    /** Move a student into `newGroupId` (or clear, if null), within MY OWN groups only. */
    const moveStudentToGroup = useCallback(async (studentId: string, newGroupId: string | null) => {
        const myGroupIds = groups.filter(g => g.owner_id === user!.id).map(g => g.id)
        if (myGroupIds.length > 0) {
            const { error: clearErr } = await supabase
                .from('group_members')
                .delete()
                .eq('student_id', studentId)
                .in('group_id', myGroupIds)
            if (clearErr) throw clearErr
        }
        if (newGroupId) {
            const { error } = await (supabase.from('group_members') as any).upsert({
                group_id: newGroupId,
                student_id: studentId,
                assigned_by: user!.id,
            }, { onConflict: 'group_id,student_id', ignoreDuplicates: true })
            if (error) throw error
        }
        await fetchAll()
    }, [groups, user?.id, fetchAll])

    /** Bulk-target a class to every member of a group (tags provenance via source_group_id). */
    const bulkAssignGroupToClass = useCallback(async (groupId: string, classId: string) => {
        const memberIds = groupMembers.filter(m => m.group_id === groupId).map(m => m.student_id)
        if (memberIds.length === 0) return
        const enrollRows = memberIds.map(sid => ({ class_id: classId, student_id: sid, source_group_id: groupId }))
        const { error } = await (supabase.from('class_enrollments') as any)
            .upsert(enrollRows, { onConflict: 'class_id,student_id', ignoreDuplicates: true })
        if (error) throw error
        await fetchAll()
    }, [groupMembers, fetchAll])

    // ── Course sharing (co-teach) & ownership transfer ───────────────────────

    const addCoTeacher = useCallback(async (classId: string, teacherId: string) => {
        const { error } = await (supabase.from('course_co_teachers') as any).insert({
            class_id: classId, teacher_id: teacherId, added_by: user!.id,
        })
        if (error) throw error
        await fetchAll()
    }, [user?.id, fetchAll])

    const removeCoTeacher = useCallback(async (classId: string, teacherId: string) => {
        const { error } = await supabase
            .from('course_co_teachers')
            .delete()
            .eq('class_id', classId)
            .eq('teacher_id', teacherId)
        if (error) throw error
        await fetchAll()
    }, [fetchAll])

    /** Full ownership handover. Admin-requested rows auto-accept (DB trigger); teacher-to-teacher needs the recipient's or admin's accept.
     *  fromTeacherId: the CURRENT owner. Teachers omit it (defaults to self);
     *  admins must pass cls.teacher_id — the DB policy requires
     *  c.teacher_id = from_teacher_id, and the auto-accept fires on
     *  check_is_facility_admin(requested_by) instead. */
    const requestCourseTransfer = useCallback(async (classId: string, toTeacherId: string, reason?: string, fromTeacherId?: string) => {
        const { error } = await (supabase.from('course_transfer_requests') as any).insert({
            class_id: classId,
            from_teacher_id: fromTeacherId || user!.id,
            to_teacher_id: toTeacherId,
            requested_by: user!.id,
            reason: reason?.trim() || null,
        })
        if (error) throw error
        await fetchAll()
    }, [user?.id, fetchAll])

    const respondToCourseTransfer = useCallback(async (requestId: string, action: 'accepted' | 'declined' | 'cancelled') => {
        const { error } = await supabase.from('course_transfer_requests').update({ status: action }).eq('id', requestId)
        if (error) throw error
        await fetchAll()
    }, [fetchAll])

    // ── Student case-ownership handover ──────────────────────────────────────

    /** toTeacherId = null releases the student back to the pool (self-serve, instant). A specific colleague needs their or admin's accept. */
    const requestStudentHandover = useCallback(async (studentId: string, toTeacherId: string | null, reason?: string) => {
        const { error } = await (supabase.from('student_teacher_transfer_requests') as any).insert({
            student_id: studentId,
            from_teacher_id: user!.id,
            to_teacher_id: toTeacherId,
            requested_by: user!.id,
            reason: reason?.trim() || null,
        })
        if (error) throw error
        await fetchAll()
    }, [user?.id, fetchAll])

    const respondToStudentHandover = useCallback(async (requestId: string, action: 'accepted' | 'declined' | 'cancelled') => {
        const { error } = await supabase.from('student_teacher_transfer_requests').update({ status: action }).eq('id', requestId)
        if (error) throw error
        await fetchAll()
    }, [fetchAll])

    const publishCourse = useCallback(async (classId: string, publish: boolean) => {
        const { data, error } = await supabase.functions.invoke('publish-course', {
            body: {
                class_id: classId,
                action: publish ? 'publish' : 'unpublish'
            }
        })
        if (error) throw error
        if (data?.error) throw new Error(data.error)
        await fetchAll()
        return data
    }, [fetchAll])

    // ── Targeted enrollment ──────────────────────────────────────────────────

    /** Set enrollment_mode on a class ('all' | 'targeted'). */
    const setEnrollmentMode = useCallback(async (classId: string, mode: 'all' | 'targeted') => {
        const { error } = await (supabase.from('classes') as any)
            .update({ enrollment_mode: mode })
            .eq('id', classId)
            .eq('facility_id', facilityId)
        if (error) throw error
        await fetchAll()
    }, [facilityId, fetchAll])

    /** Returns the list of student_ids currently enrolled in a targeted class. */
    const getEnrolledStudents = useCallback(async (classId: string): Promise<string[]> => {
        const { data, error } = await supabase
            .from('class_enrollments')
            .select('student_id')
            .eq('class_id', classId)
        if (error) throw error
        return (data || []).map((r: any) => r.student_id)
    }, [])

    /**
     * Assign specific students to a targeted class.
     * Upserts class_enrollments + student_subject_progress rows for each student.
     * Safe to call multiple times — idempotent.
     */
    const assignStudents = useCallback(async (classId: string, studentIds: string[]) => {
        if (studentIds.length === 0) return

        // Get category_id for progress row
        const { data: cls } = await (supabase.from('classes') as any)
            .select('category_id')
            .eq('id', classId)
            .maybeSingle()

        // Upsert enrollments
        const enrollRows = studentIds.map(sid => ({ class_id: classId, student_id: sid }))
        const { error: enrollErr } = await supabase
            .from('class_enrollments')
            .upsert(enrollRows, { onConflict: 'class_id,student_id', ignoreDuplicates: true })
        if (enrollErr) throw enrollErr

        // Upsert progress rows so the course appears immediately on their /learn
        if (cls?.category_id) {
            const progressRows = studentIds.map(uid => ({
                user_id: uid,
                category_id: cls.category_id,
                class_id: classId,
                hub_id: facilityId,
            }))
            const { error: progressErr } = await supabase
                .from('student_subject_progress')
                .upsert(progressRows, { onConflict: 'user_id,category_id,class_id', ignoreDuplicates: true })
            if (progressErr) throw progressErr
        }

        await fetchAll()
    }, [facilityId, fetchAll])

    /**
     * Remove a student from a targeted class.
     * Removes their class_enrollment row. Progress row is kept (never delete learning data).
     */
    const removeStudentEnrollment = useCallback(async (classId: string, studentId: string) => {
        const { error } = await supabase
            .from('class_enrollments')
            .delete()
            .eq('class_id', classId)
            .eq('student_id', studentId)
        if (error) throw error

        await fetchAll()
    }, [fetchAll])

    return {
        hub,
        classes,
        students,
        myStudentIds,
        teachers,
        progress,
        loading,
        error,
        avgMastery,
        refetch: fetchAll,
        inviteTeacher,
        inviteStudent,
        importStudents,
        createClass,
        publishCourse,
        setEnrollmentMode,
        assignStudents,
        removeStudentEnrollment,
        getEnrolledStudents,
        updateClassModulePlan,
        fetchKnowledge,
        uploadKnowledge,
        addKnowledgeText,
        deleteKnowledge,
        fetchStudentLogs,
        groups,
        groupMembers,
        createGroup,
        deleteGroup,
        moveStudentToGroup,
        bulkAssignGroupToClass,
        coTeachers,
        facilityStaff,
        addCoTeacher,
        removeCoTeacher,
        courseTransfers,
        requestCourseTransfer,
        respondToCourseTransfer,
        studentTransfers,
        requestStudentHandover,
        respondToStudentHandover,
    }
}
